/**
 * YouTube player component that uses the IFrame Player API for watch tracking.
 *
 * Extracts the video ID from a raw embed HTML string, creates a controlled
 * YouTube player, and reports max watch percentage via a callback.
 *
 * Used by:
 * - PublicEssayPage.tsx for track companion videos
 */
import { useEffect, useRef } from 'react';
import { reportVideoProgress } from '../hooks/usePageTracking';

// YouTube IFrame API types (minimal subset we need)
interface YTPlayer {
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  destroy(): void;
}

interface YTPlayerEvent {
  data: number;
  target: YTPlayer;
}

// Player states from YouTube API
const YT_PLAYING = 1;
const YT_ENDED = 0;

// Global API ready state
let apiReady = false;
let apiLoading = false;
const apiCallbacks: (() => void)[] = [];

function loadYouTubeAPI(): Promise<void> {
  if (apiReady) return Promise.resolve();
  return new Promise((resolve) => {
    apiCallbacks.push(resolve);
    if (apiLoading) return;
    apiLoading = true;

    // YouTube API calls this global when ready
    (window as any).onYouTubeIframeAPIReady = () => {
      apiReady = true;
      for (const cb of apiCallbacks) cb();
      apiCallbacks.length = 0;
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });
}

/** Extract YouTube video ID from an embed iframe HTML string. */
function extractVideoId(embedHtml: string): string | null {
  const match = embedHtml.match(/youtube(?:-nocookie)?\.com\/embed\/([a-zA-Z0-9_-]+)/);
  return match?.[1] ?? null;
}

export default function YouTubePlayer({
  embedHtml,
  videoId: videoIdProp,
  onProgress,
  onEnded,
}: {
  embedHtml?: string;
  videoId?: string;
  onProgress?: (pct: number) => void;
  onEnded?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onProgressRef = useRef(onProgress);
  const onEndedRef = useRef(onEnded);
  onProgressRef.current = onProgress;
  onEndedRef.current = onEnded;

  const videoId = videoIdProp ?? extractVideoId(embedHtml ?? '');

  function reportProgress() {
    const player = playerRef.current;
    if (!player) return;
    try {
      const current = player.getCurrentTime();
      const total = player.getDuration();
      if (total > 0) {
        const pct = Math.round((current / total) * 100);
        if (onProgressRef.current) {
          onProgressRef.current(pct);
        } else {
          reportVideoProgress(pct);
        }
      }
    } catch {
      // Player may not be ready yet
    }
  }

  useEffect(() => {
    if (!videoId || !containerRef.current) return;

    // Create a target div for the player
    const targetEl = document.createElement('div');
    targetEl.id = `yt-player-${videoId}`;
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(targetEl);

    let destroyed = false;

    loadYouTubeAPI().then(() => {
      if (destroyed) return;

      const YT = (window as any).YT;
      if (!YT?.Player) return;

      const player = new YT.Player(targetEl.id, {
        videoId,
        width: '100%',
        playerVars: {
          rel: 0,
          modestbranding: 1,
          origin: window.location.origin,
        },
        events: {
          onStateChange: (event: YTPlayerEvent) => {
            if (event.data === YT_PLAYING) {
              // Poll progress while playing
              if (!pollRef.current) {
                pollRef.current = setInterval(reportProgress, 2000);
              }
            } else {
              // Paused, ended, etc — stop polling, report final
              if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
              }
              reportProgress();
              if (event.data === YT_ENDED) {
                onEndedRef.current?.();
              }
            }
          },
        },
      });

      playerRef.current = player;
    });

    return () => {
      destroyed = true;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      try {
        playerRef.current?.destroy();
      } catch {
        // Player may already be destroyed
      }
      playerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Fallback: if we can't extract a video ID, render the raw embed
  if (!videoId) {
    if (embedHtml) return <div dangerouslySetInnerHTML={{ __html: embedHtml }} />;
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', aspectRatio: '16 / 9' }}
    />
  );
}
